import { Injectable, inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { ToastController } from '@ionic/angular/lazy';
import { PwaUpdateService } from './pwa-update.service';
import { BrowserHandlerService } from './browser-handler.service';

/**
 * PWA Install Service
 * Handles the "Add to Home Screen" / Install prompt for Progressive Web Apps.
 * Shows a toast when the app can be installed and allows users to install it.
 */
@Injectable({
  providedIn: 'root'
})
export class PwaInstallService {
  private readonly toastController = inject(ToastController);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly browserHandler = inject(BrowserHandlerService);
  private pwaUpdateService: PwaUpdateService | null = null;

  private deferredPrompt: BeforeInstallPromptEvent | null = null;
  private installPromptShown = false;
  private readonly INSTALL_PROMPT_SHOWN_KEY = 'pwa-install-prompt-shown';
  private readonly FIRST_LAUNCH_AFTER_INSTALL_KEY = 'pwa-first-launch-after-install';

  /**
   * Initialize the install prompt listener.
   * Call once from AppComponent.
   */
  init(pwaUpdateService?: PwaUpdateService): void {
    if (!isPlatformBrowser(this.platformId)) return;

    // Store reference to update service for post-install updates
    if (pwaUpdateService) {
      this.pwaUpdateService = pwaUpdateService;
    }

    // Check if this is the first launch after installation
    this.handleFirstLaunchAfterInstall();

    // Check if already installed
    if (this.isInstalled()) {
      return;
    }

    // iOS Safari doesn't support beforeinstallprompt event
    // Show iOS-specific install instructions
    if (this.browserHandler.isIOS()) {
      this.showIOSInstallPrompt();
      return;
    }

    // Listen for the beforeinstallprompt event (Android/Chrome)
    window.addEventListener('beforeinstallprompt', (e: Event) => {
      // Prevent the default mini-infobar from appearing
      e.preventDefault();
      
      // Store the event for later use
      this.deferredPrompt = e as BeforeInstallPromptEvent;
      
      // Show our custom install prompt
      this.showInstallPrompt();
    });

    // Listen for when the app is successfully installed
    window.addEventListener('appinstalled', () => {
      this.deferredPrompt = null;
      this.installPromptShown = false;
      localStorage.removeItem(this.INSTALL_PROMPT_SHOWN_KEY);
      
      // Mark that this is the first launch after installation
      // This will trigger an immediate update check when the app opens
      localStorage.setItem(this.FIRST_LAUNCH_AFTER_INSTALL_KEY, 'true');
      
      // Immediately check for updates after installation
      // This ensures the installed app uses the latest version
      setTimeout(() => {
        if (this.pwaUpdateService) {
          this.pwaUpdateService.checkAndActivateUpdateImmediately();
        }
      }, 1000);
    });
  }

  /**
   * Handle first launch after installation
   * This ensures we check for updates immediately when the app opens after installation
   */
  private handleFirstLaunchAfterInstall(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    
    // Check if app is installed (running as PWA)
    if (!this.isInstalled()) {
      return;
    }

    const isFirstLaunch = localStorage.getItem(this.FIRST_LAUNCH_AFTER_INSTALL_KEY);
    const lastUpdateCheck = localStorage.getItem('pwa-last-update-check-after-install');
    
    // Check for updates if:
    // 1. This is explicitly marked as first launch after install, OR
    // 2. We haven't checked for updates after install yet (first time opening installed app)
    if (isFirstLaunch === 'true' || !lastUpdateCheck) {
      // Clear the flag
      localStorage.removeItem(this.FIRST_LAUNCH_AFTER_INSTALL_KEY);
      
      // Mark that we've checked for updates after install
      localStorage.setItem('pwa-last-update-check-after-install', Date.now().toString());
      
      // Immediately check for and activate updates
      // This ensures the installed app uses the latest version, not the cached one
      // Wait a bit longer to ensure service worker is ready
      setTimeout(() => {
        if (this.pwaUpdateService) {
          this.pwaUpdateService.checkAndActivateUpdateImmediately();
        }
      }, 3000); // Wait 3 seconds for service worker to be ready
    }
  }

  /**
   * Check if the app is already installed
   */
  isInstalled(): boolean {
    return this.browserHandler.isInstalledPWA();
  }

  /**
   * Check if the app can be installed
   */
  canInstall(): boolean {
    // On iOS, we can always show install instructions (if not already installed)
    if (this.browserHandler.isIOS()) {
      return !this.isInstalled();
    }
    // On Android/Chrome, check for deferredPrompt
    return this.deferredPrompt !== null && !this.isInstalled();
  }

  /**
   * Show iOS-specific install instructions
   * iOS Safari doesn't support beforeinstallprompt, so we need to guide users manually
   */
  private async showIOSInstallPrompt(): Promise<void> {
    // Don't show if already shown recently (within 7 days)
    const lastShown = localStorage.getItem(this.INSTALL_PROMPT_SHOWN_KEY);
    if (lastShown) {
      const daysSinceShown = (Date.now() - parseInt(lastShown, 10)) / (1000 * 60 * 60 * 24);
      if (daysSinceShown < 7) {
        return; // Don't show again for 7 days
      }
    }

    // Don't show if already shown in this session
    if (this.installPromptShown) {
      return;
    }

    this.installPromptShown = true;
    localStorage.setItem(this.INSTALL_PROMPT_SHOWN_KEY, Date.now().toString());

    const toast = await this.toastController.create({
      header: 'Installing the app',
      message: '1. Tap the Share icon (square with arrow up) at the bottom in Safari\n\n2. Scroll down and select "Add to Home screen"\n\n3. Confirm the installation',
      duration: 0, // Don't auto-dismiss
      position: 'bottom',
      mode: 'ios',
      color: 'primary',
      swipeGesture: 'vertical',
      cssClass: 'ios-install-prompt',
      buttons: [
        {
          text: 'Got it',
          role: 'cancel',
          handler: () => {
            this.installPromptShown = false;
          }
        }
      ]
    });

    await toast.present();
  }

  /**
   * Show the install prompt toast
   */
  private async showInstallPrompt(): Promise<void> {
    // Don't show if already shown recently (within 7 days)
    const lastShown = localStorage.getItem(this.INSTALL_PROMPT_SHOWN_KEY);
    if (lastShown) {
      const daysSinceShown = (Date.now() - parseInt(lastShown, 10)) / (1000 * 60 * 60 * 24);
      if (daysSinceShown < 7) {
        return; // Don't show again for 7 days
      }
    }

    // Don't show if already shown in this session
    if (this.installPromptShown) {
      return;
    }

    this.installPromptShown = true;
    localStorage.setItem(this.INSTALL_PROMPT_SHOWN_KEY, Date.now().toString());

    const toast = await this.toastController.create({
      message: 'Install the app as a WebApp?',
      duration: 0, // Don't auto-dismiss
      position: 'bottom',
      mode: 'ios',
      color: 'primary',
      swipeGesture: 'vertical',
      buttons: [
        {
          text: 'Later',
          role: 'cancel',
          handler: () => {
            this.installPromptShown = false;
          }
        },
        {
          text: 'Install',
          role: 'confirm',
          handler: () => {
            this.promptInstall();
          }
        }
      ]
    });

    await toast.present();
  }

  /**
   * Prompt the user to install the app
   */
  async promptInstall(): Promise<void> {
    if (!this.deferredPrompt) {
      return;
    }

    try {
      // Show the install prompt
      await this.deferredPrompt.prompt();
      
      // Wait for the user's response
      const { outcome } = await this.deferredPrompt.userChoice;
      
      if (outcome === 'accepted') {
        // User accepted the install prompt
        console.log('User accepted the install prompt');
      } else {
        // User dismissed the install prompt
        console.log('User dismissed the install prompt');
      }
      
      // Clear the deferred prompt
      this.deferredPrompt = null;
      this.installPromptShown = false;
    } catch (error) {
      console.error('Error showing install prompt:', error);
      this.deferredPrompt = null;
      this.installPromptShown = false;
    }
  }

  /**
   * Manually trigger the install prompt (e.g., from a button)
   * This can be called from anywhere in the app
   */
  async showInstallButton(): Promise<boolean> {
    if (!this.canInstall()) {
      return false;
    }

    await this.promptInstall();
    return true;
  }
}

/**
 * Type definition for the beforeinstallprompt event
 */
interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{
    outcome: 'accepted' | 'dismissed';
    platform: string;
  }>;
}
